import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Building2, Users, CreditCard, TrendingUp, Sparkles, ArrowRight,
  Activity, Heart, BarChart3, LifeBuoy, AlertCircle, Plus, Tag,
  Palette, Sliders, Database, Download, Server, CheckCircle2, Pause,
  Hourglass, Bell, Bot,
} from "lucide-react";

/* ============== KPI CARD (premium) ============== */
function KpiCard({ label, value, sub, icon: Icon, tone = "violet", testid }) {
  const tones = {
    violet:  { bg: "from-violet-500/10 to-violet-500/5", ring: "ring-violet-200", icon: "from-violet-500 to-violet-600", text: "text-violet-700" },
    blue:    { bg: "from-blue-500/10 to-blue-500/5",     ring: "ring-blue-200",   icon: "from-blue-500 to-blue-600",     text: "text-blue-700" },
    emerald: { bg: "from-emerald-500/10 to-emerald-500/5", ring: "ring-emerald-200", icon: "from-emerald-500 to-emerald-600", text: "text-emerald-700" },
    amber:   { bg: "from-amber-500/10 to-amber-500/5",   ring: "ring-amber-200",  icon: "from-amber-500 to-amber-600",   text: "text-amber-700" },
    fuchsia: { bg: "from-fuchsia-500/10 to-fuchsia-500/5", ring: "ring-fuchsia-200", icon: "from-fuchsia-500 to-fuchsia-600", text: "text-fuchsia-700" },
    orange:  { bg: "from-orange-500/10 to-orange-500/5", ring: "ring-orange-200", icon: "from-orange-500 to-orange-600", text: "text-orange-700" },
    rose:    { bg: "from-rose-500/10 to-rose-500/5",     ring: "ring-rose-200",   icon: "from-rose-500 to-rose-600",     text: "text-rose-700" },
    slate:   { bg: "from-slate-500/10 to-slate-500/5",   ring: "ring-slate-200",  icon: "from-slate-700 to-slate-900",   text: "text-slate-700" },
  };
  const t = tones[tone];
  return (
    <Card className={`relative overflow-hidden p-5 border-slate-200 shadow-sm bg-gradient-to-br ${t.bg} ring-1 ${t.ring}`} data-testid={testid}>
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500">{label}</div>
          <div className="mt-2 text-3xl font-bold font-[Outfit] tracking-tight text-slate-900 tabular-nums">{value}</div>
          {sub && <div className={`mt-1 text-[11px] font-semibold ${t.text}`}>{sub}</div>}
        </div>
        <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${t.icon} text-white flex items-center justify-center shadow-md shrink-0`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </Card>
  );
}

/* ============== QUICK ACTION ============== */
function QuickAction({ icon: Icon, label, to, onClick, testid }) {
  const Inner = (
    <div className="flex items-center gap-3 p-3.5 rounded-xl bg-white border border-slate-200 hover:border-violet-300 hover:shadow-md transition-all cursor-pointer group" data-testid={testid}>
      <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-violet-100 to-fuchsia-100 text-violet-700 flex items-center justify-center group-hover:from-violet-500 group-hover:to-fuchsia-500 group-hover:text-white transition-all">
        <Icon className="w-4 h-4" />
      </div>
      <span className="text-sm font-semibold text-slate-800 flex-1">{label}</span>
      <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-violet-600 group-hover:translate-x-0.5 transition-all" />
    </div>
  );
  return to ? <Link to={to}>{Inner}</Link> : <button onClick={onClick} className="w-full text-left">{Inner}</button>;
}

/* ============== MAIN PAGE ============== */
export default function PlatformDashboard() {
  const nav = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/master/tenants").then(({ data }) => setTenants(data || [])).catch(() => setTenants([])).finally(() => setLoading(false));
  }, []);

  const total       = tenants.length;
  const active      = tenants.filter(t => t.status === "active").length;
  const trial       = tenants.filter(t => t.status === "trial").length;
  const suspended   = tenants.filter(t => t.status === "suspended").length;
  const totalUsers  = tenants.reduce((a, t) => a + (t.users_count || 0), 0);
  const upcoming    = tenants.filter(t => {
    if (!t.trial_ends_at) return false;
    const days = (new Date(t.trial_ends_at) - Date.now()) / (1000 * 60 * 60 * 24);
    return days >= 0 && days <= 14;
  }).length;

  // Mocked finance/support metrics — wire to real APIs later
  const mrr = active * 49 + trial * 0; // placeholder USD
  const openTickets = 7;

  // Recent tenants (last 5 by created_at if present, else first 5)
  const recent = [...tenants].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || "")).slice(0, 5);

  return (
    <div className="space-y-6" data-testid="platform-dashboard">
      {/* HEADER */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-violet-700">Platform Console</div>
          <h1 className="text-3xl font-bold font-[Outfit] tracking-tight text-slate-900 mt-1">Good day, Owner.</h1>
          <p className="text-sm text-slate-600 mt-1.5">Pulse of every Qrew HR client — at a glance.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => nav("/master/tenants")} className="border-slate-200" data-testid="pd-view-clients">
            <Building2 className="w-4 h-4 mr-1.5" /> View all clients
          </Button>
          <Button onClick={() => nav("/master/tenants")} className="qh-grad-bg text-white font-semibold" data-testid="pd-new-tenant">
            <Plus className="w-4 h-4 mr-1.5" /> New Tenant
          </Button>
        </div>
      </div>

      {/* KPI CARDS — 8 */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard label="Total Clients"     value={total}       sub={`${active + trial} active`} icon={Building2}    tone="violet"  testid="kpi-total-clients" />
        <KpiCard label="Active Clients"    value={active}      sub="Paying customers"          icon={CheckCircle2}  tone="emerald" testid="kpi-active-clients" />
        <KpiCard label="Trial Clients"     value={trial}       sub="In free trial"             icon={Hourglass}     tone="amber"   testid="kpi-trial-clients" />
        <KpiCard label="Suspended"         value={suspended}   sub="Action needed"             icon={Pause}         tone="rose"    testid="kpi-suspended" />
        <KpiCard label="Total Users"       value={totalUsers.toLocaleString()} sub="Across tenants" icon={Users}     tone="blue"    testid="kpi-total-users" />
        <KpiCard label="MRR"               value={`$${mrr.toLocaleString()}`}  sub="Estimated · USD" icon={TrendingUp} tone="fuchsia" testid="kpi-mrr" />
        <KpiCard label="Upcoming Renewals" value={upcoming}    sub="Next 14 days"              icon={CreditCard}    tone="orange"  testid="kpi-renewals" />
        <KpiCard label="Open Tickets"      value={openTickets} sub="Needs response"            icon={LifeBuoy}      tone="slate"   testid="kpi-open-tickets" />
      </div>

      {/* MAIN GRID */}
      <div className="grid lg:grid-cols-3 gap-5">
        {/* LEFT — Recent Tenants table */}
        <Card className="lg:col-span-2 p-5 border-slate-200 shadow-sm" data-testid="pd-recent-tenants">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500">Recent Tenants</div>
              <div className="text-base font-semibold font-[Outfit] text-slate-900 mt-0.5">Latest client onboardings</div>
            </div>
            <Link to="/master/tenants" className="text-xs font-semibold text-violet-700 hover:underline inline-flex items-center gap-1">
              View all <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          <div className="overflow-x-auto -mx-5">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-[0.12em] text-slate-500 border-b border-slate-100">
                  <th className="px-5 py-2 font-bold">Organization</th>
                  <th className="px-5 py-2 font-bold">Plan</th>
                  <th className="px-5 py-2 font-bold">Status</th>
                  <th className="px-5 py-2 font-bold text-right">Users</th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={4} className="px-5 py-8 text-center text-slate-400">Loading…</td></tr>}
                {!loading && recent.length === 0 && <tr><td colSpan={4} className="px-5 py-8 text-center text-slate-400">No tenants yet</td></tr>}
                {recent.map((t) => (
                  <tr key={t.id} className="border-b border-slate-50 hover:bg-violet-50/40 transition" data-testid={`pd-tenant-${t.slug}`}>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white flex items-center justify-center font-bold text-sm shrink-0">
                          {(t.name?.[0] || "T").toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <div className="font-semibold text-slate-900 truncate">{t.name}</div>
                          <div className="text-[11px] text-slate-500 font-mono truncate">{t.slug}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      <Badge className="capitalize bg-slate-100 text-slate-700 border border-slate-200">{t.plan}</Badge>
                    </td>
                    <td className="px-5 py-3">
                      <Badge className={`capitalize border-0 ${
                        t.status === "active"    ? "bg-emerald-100 text-emerald-700" :
                        t.status === "trial"     ? "bg-amber-100 text-amber-700"   :
                        t.status === "suspended" ? "bg-rose-100 text-rose-700"     :
                                                    "bg-slate-100 text-slate-700"
                      }`}>{t.status}</Badge>
                    </td>
                    <td className="px-5 py-3 text-right font-semibold text-slate-900 tabular-nums">{t.users_count ?? 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        {/* RIGHT — Quick Actions */}
        <Card className="p-5 border-slate-200 shadow-sm" data-testid="pd-quick-actions">
          <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500">Quick Actions</div>
          <div className="text-base font-semibold font-[Outfit] text-slate-900 mt-0.5 mb-4">What's next?</div>
          <div className="space-y-2">
            <QuickAction icon={Plus}     label="Create Tenant"        to="/master/tenants"        testid="qa-create-tenant" />
            <QuickAction icon={Tag}      label="Create Plan"           to="/master/pricing"        testid="qa-create-plan" />
            <QuickAction icon={Palette}  label="Open Brand Manager"    to="/master/brand-content"  testid="qa-brand-mgr" />
            <QuickAction icon={Sliders}  label="Open Feature Controls" to="/master/feature-controls" testid="qa-feature-ctrl" />
            <QuickAction icon={Database} label="Backup & Restore"      to="/master/backups"        testid="qa-backup" />
            <QuickAction icon={Download} label="Export Clients (CSV)"  onClick={async () => {
              try {
                const API_URL = process.env.REACT_APP_BACKEND_URL;
                const token = localStorage.getItem("hreye_token");
                const res = await fetch(`${API_URL}/api/master/tenants-export`, { headers: { Authorization: `Bearer ${token}` } });
                const blob = await res.blob();
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a"); a.href = url; a.download = "clients.csv"; a.click(); URL.revokeObjectURL(url);
              } catch {}
            }} testid="qa-export-clients" />
          </div>
        </Card>
      </div>

      {/* SECONDARY — 4 widget cards */}
      <div className="grid lg:grid-cols-2 gap-5">
        {/* Client Health */}
        <Card className="p-5 border-slate-200 shadow-sm" data-testid="pd-client-health">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 text-white flex items-center justify-center"><Heart className="w-4 h-4" /></div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500">Client Health</div>
                <div className="text-base font-semibold font-[Outfit] text-slate-900">Engagement at a glance</div>
              </div>
            </div>
            <Link to="/master/client-health" className="text-xs font-semibold text-emerald-700 hover:underline inline-flex items-center gap-1">Details <ArrowRight className="w-3 h-3" /></Link>
          </div>
          <div className="grid grid-cols-3 gap-3">
            {[
              { tier: "Healthy",  count: Math.max(0, active - 1), color: "bg-emerald-500", text: "text-emerald-700", bg: "bg-emerald-50" },
              { tier: "At-Risk",  count: Math.min(2, total),      color: "bg-amber-500",   text: "text-amber-700",   bg: "bg-amber-50" },
              { tier: "Churning", count: suspended,                color: "bg-rose-500",    text: "text-rose-700",    bg: "bg-rose-50" },
            ].map((c) => (
              <div key={c.tier} className={`p-3 rounded-xl ${c.bg} border border-slate-100`}>
                <div className="flex items-center gap-1.5">
                  <span className={`w-1.5 h-1.5 rounded-full ${c.color}`} />
                  <span className={`text-[10px] uppercase tracking-wider font-bold ${c.text}`}>{c.tier}</span>
                </div>
                <div className="mt-1.5 text-2xl font-bold font-[Outfit] text-slate-900 tabular-nums">{c.count}</div>
              </div>
            ))}
          </div>
        </Card>

        {/* Feature Adoption */}
        <Card className="p-5 border-slate-200 shadow-sm" data-testid="pd-feature-adoption">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 text-white flex items-center justify-center"><BarChart3 className="w-4 h-4" /></div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500">Feature Adoption</div>
                <div className="text-base font-semibold font-[Outfit] text-slate-900">Top modules across tenants</div>
              </div>
            </div>
          </div>
          <div className="space-y-2.5">
            {[
              { f: "Attendance",   pct: 92, color: "bg-emerald-500" },
              { f: "Payroll",      pct: 78, color: "bg-blue-500" },
              { f: "Recruitment",  pct: 64, color: "bg-violet-500" },
              { f: "Performance",  pct: 51, color: "bg-fuchsia-500" },
              { f: "AI Copilot",   pct: 39, color: "bg-orange-500" },
            ].map((m) => (
              <div key={m.f}>
                <div className="flex items-center justify-between text-[12px] mb-1">
                  <span className="font-semibold text-slate-700">{m.f}</span>
                  <span className="text-slate-500 tabular-nums">{m.pct}%</span>
                </div>
                <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                  <div className={`h-full ${m.color}`} style={{ width: `${m.pct}%` }} />
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* System Health */}
        <Card className="p-5 border-slate-200 shadow-sm" data-testid="pd-system-health">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white flex items-center justify-center"><Server className="w-4 h-4" /></div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500">System Health</div>
                <div className="text-base font-semibold font-[Outfit] text-slate-900">All services nominal</div>
              </div>
            </div>
            <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-emerald-700"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> Operational</span>
          </div>
          <div className="grid grid-cols-3 gap-3">
            {[
              { svc: "API",      v: "99.98%",  sub: "30d uptime",  tone: "emerald" },
              { svc: "Database", v: "12 ms",   sub: "p95 latency", tone: "blue" },
              { svc: "Queue",    v: "0",       sub: "Jobs lagging", tone: "violet" },
            ].map((s) => {
              const colors = {
                emerald: "text-emerald-700 bg-emerald-50 border-emerald-100",
                blue:    "text-blue-700 bg-blue-50 border-blue-100",
                violet:  "text-violet-700 bg-violet-50 border-violet-100",
              };
              return (
                <div key={s.svc} className={`p-3 rounded-xl border ${colors[s.tone]}`}>
                  <div className="text-[10px] uppercase tracking-wider font-bold text-slate-600">{s.svc}</div>
                  <div className="mt-1 text-xl font-bold font-[Outfit] tabular-nums">{s.v}</div>
                  <div className="text-[10px] text-slate-500 mt-0.5">{s.sub}</div>
                </div>
              );
            })}
          </div>
        </Card>

        {/* Support Queue */}
        <Card className="p-5 border-slate-200 shadow-sm" data-testid="pd-support-queue">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-orange-500 to-rose-500 text-white flex items-center justify-center"><LifeBuoy className="w-4 h-4" /></div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500">Support Queue</div>
                <div className="text-base font-semibold font-[Outfit] text-slate-900">Pending tickets</div>
              </div>
            </div>
            <Link to="/master/tickets" className="text-xs font-semibold text-orange-700 hover:underline inline-flex items-center gap-1">Open queue <ArrowRight className="w-3 h-3" /></Link>
          </div>
          <div className="space-y-2.5">
            {[
              { id: "T-0091", title: "Payroll export missing fields", tenant: "Acme HR",    sev: "high",   ago: "12m" },
              { id: "T-0090", title: "SAML login redirect loop",     tenant: "Globex Inc", sev: "medium", ago: "1h"  },
              { id: "T-0089", title: "Logo upload fails (URL only)", tenant: "Initech",    sev: "low",    ago: "3h"  },
            ].map((t) => (
              <div key={t.id} className="flex items-center gap-3 p-3 rounded-xl border border-slate-100 hover:bg-slate-50 transition" data-testid={`pd-ticket-${t.id}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${t.sev === "high" ? "bg-rose-500" : t.sev === "medium" ? "bg-amber-500" : "bg-slate-400"}`} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-slate-900 truncate">{t.title}</div>
                  <div className="text-[11px] text-slate-500">{t.tenant} · <span className="font-mono">{t.id}</span></div>
                </div>
                <span className="text-[10px] text-slate-500 tabular-nums">{t.ago}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* CLIENT GROWTH (sparkline-style band) */}
      <Card className="p-5 border-slate-200 shadow-sm" data-testid="pd-client-growth">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white flex items-center justify-center"><TrendingUp className="w-4 h-4" /></div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500">Client Growth</div>
              <div className="text-base font-semibold font-[Outfit] text-slate-900">New tenants per month</div>
            </div>
          </div>
          <Badge className="bg-violet-100 text-violet-800 border border-violet-200 text-[10px] font-bold uppercase tracking-wider">Last 6 months</Badge>
        </div>
        <div className="flex items-end gap-2 h-28">
          {[3, 5, 4, 7, 9, total || 12].map((v, i) => {
            const max = Math.max(3, 5, 4, 7, 9, total || 12);
            const h = Math.round((v / max) * 100);
            const isLast = i === 5;
            return (
              <div key={i} className="flex-1 flex flex-col items-center gap-1.5">
                <div className={`w-full rounded-t-md ${isLast ? "bg-gradient-to-t from-violet-500 to-fuchsia-500" : "bg-slate-200"} transition-all`} style={{ height: `${h}%` }} />
                <span className="text-[10px] text-slate-500 font-semibold">{["Dec","Jan","Feb","Mar","Apr","May"][i]}</span>
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}
